import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Button, Chip, CircularProgress, IconButton, MenuItem, TextField, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Alert } from "@material-ui/lab";
import { Close, ViewWeekOutlined } from "@material-ui/icons";
import { v4 as uuid } from "uuid";
import { fetchKanbanContext, moveKanbanTicket, normalizeStageTarget } from "../../services/kanban";

const useStyles = makeStyles(theme => ({
  panel: { width: "100%", boxSizing: "border-box", padding: "12px 16px", background: theme.crm.surface, color: theme.crm.text, borderTop: `2px solid ${theme.crm.accent}`, maxHeight: "min(420px, 60vh)", overflowY: "auto", [theme.breakpoints.down("xs")]: { padding: 10 } },
  header: { display: "flex", alignItems: "center", gap: 10, marginBottom: 12, "& > svg": { color: theme.crm.accent } },
  title: { flex: 1, minWidth: 0, "& strong": { fontSize: 14 }, "& small": { display: "block", color: theme.crm.muted, fontSize: 12, overflowWrap: "anywhere" } },
  close: { flexShrink: 0, color: theme.crm.muted, border: `1px solid ${theme.crm.border}`, borderRadius: 8 },
  closeWrap: { display: "inline-flex", flexShrink: 0 },
  current: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 12, fontSize: 12, color: theme.crm.muted },
  chip: { fontWeight: 700, borderRadius: 6 },
  state: { display: "flex", alignItems: "center", gap: 10, margin: "14px 0", fontSize: 13 },
  alert: { marginBottom: 12 },
  actions: { display: "flex", alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end", gap: 8, marginTop: 12 },
}));
const isUnknown = error => !error.response || error.response.status >= 500;
const errorText = error => typeof error.response?.data?.error === "string" ? error.response.data.error : "Não foi possível carregar o Kanban deste atendimento.";

export default forwardRef(function InlineKanbanComposer({ ticketId, contact, blocked = false, onCancel, onSaved }, ref) {
  const classes = useStyles();
  const [context, setContext] = useState(null), [target, setTarget] = useState("");
  const [loading, setLoading] = useState(true), [saving, setSaving] = useState(false);
  const [error, setError] = useState(""), [uncertain, setUncertain] = useState(false);
  const state = useRef({ live: false, read: 0, busy: false, intent: null, uncertain: false });

  const load = useCallback(async (preserveTarget = false, notice = "") => {
    const current = state.current, read = ++current.read;
    setLoading(true); setError("");
    try {
      const { data } = await fetchKanbanContext(ticketId);
      if (!current.live || read !== current.read) return;
      if (Number(data?.ticketId) !== Number(ticketId) || Number(data?.contactId) !== Number(contact.id)
        || !Number.isSafeInteger(data.version) || data.version < 0 || !Array.isArray(data.stages)
        || !["open", "pending"].includes(data.status)) throw new Error("INVALID_CONTEXT");
      setContext(data);
      const exists = value => value === "none" || data.stages.some(stage => String(stage.id) === String(value));
      setTarget(previous => preserveTarget && exists(previous) ? previous
        : !data.hasMultipleStages && data.stageTagId != null && exists(data.stageTagId) ? String(data.stageTagId) : "");
      setError(notice);
    } catch (err) {
      if (current.live && read === current.read) { setContext(null); setError(errorText(err)); }
    } finally {
      if (current.live && read === current.read) setLoading(false);
    }
  }, [ticketId, contact.id]);

  useEffect(() => {
    const current = state.current; current.live = true;
    load();
    return () => { current.live = false; current.read += 1; };
  }, [load]);

  const close = () => {
    if (state.current.busy) return false;
    onCancel({ uncertain: state.current.uncertain });
    return true;
  };
  useImperativeHandle(ref, () => ({ cancel: close }));

  async function submit(event) {
    event.preventDefault();
    const current = state.current;
    if (current.busy || blocked || loading || !context || target === "") return;
    const stageTagId = normalizeStageTarget(target);
    if (!current.intent && !context.hasMultipleStages && stageTagId === context.stageTagId) return;
    current.busy = true; setSaving(true); setError("");
    let completed = false;
    const intent = current.intent || { ticketId, stageTagId, expectedVersion: context.version, requestId: uuid() };
    current.intent = intent;
    try {
      let response;
      try { response = await moveKanbanTicket(intent); }
      catch (err) { if (!isUnknown(err)) throw err; response = await moveKanbanTicket(intent); }
      if (!current.live) return;
      const result = response.data;
      if (Number(result?.ticketId) !== Number(ticketId) || result.stageTagId !== intent.stageTagId
        || !Number.isSafeInteger(result.version)) throw new Error("INVALID_MOVE_RESPONSE");
      current.intent = null; current.uncertain = false; completed = true;
      const destination = context.stages.find(stage => Number(stage.id) === intent.stageTagId);
      onSaved({ ...result, stageName: destination?.name || "Sem etapa" });
    } catch (err) {
      if (!current.live) return;
      if (err.response?.status === 409) {
        current.intent = null; current.uncertain = false; setUncertain(false);
        await load(true, "O atendimento mudou. Confira a etapa atual antes de confirmar novamente.");
      } else if (isUnknown(err)) {
        current.uncertain = true; setUncertain(true);
        setError("Não foi possível confirmar a movimentação. O card pode ter sido movido; tente novamente para conferir a mesma operação.");
      } else {
        current.intent = null; current.uncertain = false; setUncertain(false);
        setError(errorText(err));
        if ([403, 404].includes(err.response?.status)) setContext(null);
      }
    } finally {
      current.busy = false;
      if (current.live && !completed) setSaving(false);
    }
  }

  const active = context?.stages.find(stage => Number(stage.id) === context.stageTagId);
  const currentName = context?.stageTagId == null ? "Sem etapa" : active?.name || "Etapa indisponível";
  const unchanged = context && !context.hasMultipleStages && target !== "" && normalizeStageTarget(target) === context.stageTagId;
  return <form className={classes.panel} aria-label="Organizar no Kanban" noValidate onSubmit={submit}
    onKeyDown={event => { if (event.key === "Enter" && event.target.tagName !== "BUTTON") { event.stopPropagation(); event.preventDefault(); } }}>
    <div className={classes.header}><ViewWeekOutlined /><div className={classes.title}><strong>Kanban</strong><small>{contact.name || "Cliente atual"} · Atendimento #{ticketId}</small></div><Tooltip title="Fechar Kanban"><span className={classes.closeWrap}><IconButton size="small" className={classes.close} aria-label="Fechar Kanban" disabled={saving} onClick={close}><Close /></IconButton></span></Tooltip></div>
    {error && <Alert className={classes.alert} severity={uncertain || context ? "warning" : "error"}>{error}</Alert>}
    {loading ? <div className={classes.state}><CircularProgress size={20} aria-label="Carregando Kanban" /><span>Carregando etapas…</span></div> : context ? <>
      <div className={classes.current}><span>Etapa atual</span><Chip className={classes.chip} size="small" variant="outlined" label={currentName} style={{ borderColor: active?.color || undefined }} /></div>
      {context.hasMultipleStages && <Alert className={classes.alert} severity="warning">Há mais de uma etapa vinculada. Escolha a etapa correta para unificar o card.</Alert>}
      {context.stages.length ? <TextField id="inline-kanban-target" select fullWidth variant="outlined" size="small" label="Etapa de destino" value={target} autoFocus disabled={saving || blocked || uncertain} InputLabelProps={{ shrink: true }} onChange={event => setTarget(String(event.target.value))}>
        <MenuItem value="" disabled>Selecione uma etapa</MenuItem>
        <MenuItem value="none">Sem etapa</MenuItem>
        {context.stages.map(stage => <MenuItem key={stage.id} value={String(stage.id)}>{stage.name}</MenuItem>)}
      </TextField> : <Alert severity="info">Nenhuma etapa disponível. Configure as etapas na tela Kanban.</Alert>}
    </> : null}
    <div className={classes.actions}>
      {!loading && !uncertain && (error || !context?.stages.length) && <Button disabled={saving} onClick={() => load(true)}>Atualizar etapas</Button>}
      <Button disabled={saving} onClick={close}>Cancelar</Button>
      <Button type="submit" variant="contained" color="primary" startIcon={saving ? <CircularProgress size={16} /> : <ViewWeekOutlined />}
        disabled={saving || loading || blocked || !context?.stages.length || target === "" || (unchanged && !uncertain)}>
        {saving ? "Confirmando…" : uncertain ? "Tentar novamente" : context?.stageTagId == null && !context?.hasMultipleStages ? "Adicionar ao Kanban" : "Confirmar movimentação"}
      </Button>
    </div>
  </form>;
});
